import LocalizedTextField from '@components/Customization/LocalizedTextField';
import NumberField from '@components/Customization/NumberField';
import ToggleRow from '@components/Customization/ToggleRow';
import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import { useLocalization } from '@lib/localization';

import {
  Box, Button, Chip, Divider, Drawer, FormControl, FormHelperText, FormLabel, IconButton, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
  stepLabelStyles, stepNumberStyles, typeCardOptionSelectedStyles, typeCardOptionStyles, typeGridStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  barcodeFormats, formInputTypes, hasText, newEntityId, type BarcodeFormat, type FormInputType,
  type PoiField, type PoiFieldOption,
} from '@store/api/poiTypeEndpoints';
import { useEffect, useState } from 'react';
import { PlusIcon, StarIcon, StarOIcon, TimesIcon, TrashIcon } from 'react-line-awesome';

/** `'d'` is the pseudo-group for fields sitting directly on the type. */
export type GroupTarget = 'd' | number;

interface Props {
  open: boolean;
  /** The field being edited, or null when adding a new one. */
  field: PoiField | null;
  group: GroupTarget;
  /** Group names for the "which section" picker, in the type's own order. */
  groupNames: string[];
  typeName: string;
  saving: boolean;
  onClose: () => void;
  onSave: (field: PoiField, group: GroupTarget) => void;
}

const emptyField = (): PoiField => ({
  label: {}, type: 'Text', required: false, readOnly: false, showOnReports: true, multiline: false,
});

/**
 * Rounds whatever a numeric field is holding, for the moment decimals are turned off.
 *
 * Without this the field keeps a fractional value the API will reject, and the separator
 * keys are now disabled, so the only way out would be to clear the box and start again.
 */
const whole = (field: PoiField): Partial<PoiField> => ({
  ...(typeof field.value === 'number' ? { value: Math.round(field.value) } : {}),
  ...(field.min === undefined ? {} : { min: Math.round(field.min) }),
  ...(field.max === undefined ? {} : { max: Math.round(field.max) }),
});

/** A choice field's `value` is one id or a list of them; both read as a list here. */
const chosenIds = (field: Pick<PoiField, 'value'>): string[] => {
  if (Array.isArray(field.value)) return field.value;

  return typeof field.value === 'string' && field.value ? [field.value] : [];
};

/** Numbered section header, mirroring the prototype's three-step drawer. */
const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <Stack direction="row" sx={stepLabelStyles}>
    <Box component="span" sx={stepNumberStyles}>{n}</Box>
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
      {children}
    </Typography>
  </Stack>
);

const FieldDrawer: React.FC<Props> = ({
  open, field, group, groupNames, typeName, saving, onClose, onSave,
}) => {
  const { t } = useLocalization();

  const [draft, setDraft] = useState<PoiField>(emptyField());
  const [target, setTarget] = useState<GroupTarget>('d');

  useEffect(() => {
    if (!open) return;
    setDraft(field ? { ...field } : emptyField());
    setTarget(group);
  }, [open, field, group]);

  const patch = (changes: Partial<PoiField>) => setDraft((current) => ({ ...current, ...changes }));

  /**
   * Switching type drops the previous type's configuration rather than carrying it along:
   * the API rejects a key sent against the wrong type, so keeping it would only produce a
   * 400 at save time.
   */
  const changeType = (type: FormInputType) => setDraft((current) => ({
    label: current.label,
    help: current.help,
    placeholder: current.placeholder,
    required: current.required,
    readOnly: current.readOnly,
    showOnReports: current.showOnReports,
    id: current.id,
    type,
    ...(type === 'Text' ? { multiline: false } : {}),
    ...(type === 'Number' || type === 'Range' ? { decimal: false } : {}),
    ...(type === 'Range' ? { min: 0, max: 100 } : {}),
    ...(type === 'Toggle' ? { value: false } : {}),
    ...(type === 'Barcode' ? { formats: [] as BarcodeFormat[] } : {}),
    // Switching between the two choice types keeps both the options and what was marked
    // as default; only the shape of `value` changes — one id for List, a list for MultiList.
    ...(type === 'List' || type === 'MultiList'
      ? {
        options: current.options?.length ? current.options : [{ id: newEntityId(), label: {} }],
        value: type === 'List' ? chosenIds(current)[0] : chosenIds(current),
      }
      : {}),
  }));

  const isChoice = draft.type === 'List' || draft.type === 'MultiList';
  const options = draft.options ?? [];
  const chosen = chosenIds(draft);

  const setOption = (index: number, option: PoiFieldOption) =>
    patch({ options: options.map((current, i) => (i === index ? option : current)) });

  const removeOption = (index: number) => {
    const removed = options[index];
    const kept = chosen.filter((id) => id !== removed.id);

    patch({
      options: options.filter((_, i) => i !== index),
      // An option id left behind would point at nothing and be rejected on save.
      value: draft.type === 'List' ? kept[0] : kept,
    });
  };

  /** Defaults are held by option id — minted on add, so this works before the first save. */
  const toggleDefault = (option: PoiFieldOption) => {
    if (!option.id) return;

    const isOn = chosen.includes(option.id);

    if (draft.type === 'List') {
      patch({ value: isOn ? undefined : option.id });
      return;
    }

    patch({ value: isOn ? chosen.filter((id) => id !== option.id) : [...chosen, option.id] });
  };

  const canSave = hasText(draft.label) && !saving
    && (!isChoice || options.every((option) => hasText(option.label)));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 560 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {field ? t('Customization_EditField') : t('Customization_NewField')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{typeName}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          <Step n={1}>{t('Customization_FieldType')}</Step>
          <Box sx={{ ...typeGridStyles, mb: 3.5 }}>
            {formInputTypes.map((type) => (
              <Stack
                key={type}
                sx={{ ...typeCardOptionStyles, ...(draft.type === type ? typeCardOptionSelectedStyles : {}) }}
                onClick={() => changeType(type)}
              >
                <FieldTypeIcon type={type} size={32} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.2 }}>
                  {t(`Customization_FieldType_${type}`)}
                </Typography>
              </Stack>
            ))}
          </Box>

          <Step n={2}>{t('Customization_Configure')}</Step>
          <Stack spacing={2.5}>
            <TextField
              select
              size="small"
              label={t('Customization_Group')}
              value={String(target)}
              onChange={(event) => setTarget(event.target.value === 'd' ? 'd' : Number(event.target.value))}
              helperText={t('Customization_GroupHelp')}
            >
              <MenuItem value="d">{t('Customization_NoGroup')}</MenuItem>
              {groupNames.map((name, index) => (
                <MenuItem key={index} value={String(index)}>{name}</MenuItem>
              ))}
            </TextField>

            <LocalizedTextField
              required
              autoFocus
              label={t('Customization_Label')}
              value={draft.label}
              onChange={(label) => patch({ label })}
              helperText={t('Customization_LabelHelp')}
            />

            <TextField
              size="small"
              label={t('Customization_HelpText')}
              value={draft.help ?? ''}
              onChange={(event) => patch({ help: event.target.value })}
            />

            {['Text', 'Number', 'Barcode'].includes(draft.type) && (
              <TextField
                size="small"
                label={t('Customization_Placeholder')}
                value={draft.placeholder ?? ''}
                onChange={(event) => patch({ placeholder: event.target.value })}
              />
            )}

            {/* The three types that gained a default when the per-type keys collapsed into
                `value`. Range and Toggle set theirs in their own blocks below, and a choice
                field marks it with the star on an option. */}
            {draft.type === 'Number' && (
              <NumberField
                fullWidth
                size="small"
                label={t('Common_Default')}
                allowDecimal={Boolean(draft.decimal)}
                value={typeof draft.value === 'number' ? draft.value : undefined}
                onChange={(value) => patch({ value })}
              />
            )}

            {(draft.type === 'Text' || draft.type === 'Barcode') && (
              <TextField
                fullWidth
                size="small"
                label={t('Common_Default')}
                multiline={draft.type === 'Text' && Boolean(draft.multiline)}
                value={typeof draft.value === 'string' ? draft.value : ''}
                onChange={(event) => patch({ value: event.target.value || undefined })}
              />
            )}

            {draft.type === 'Text' && (
              <>
                <ToggleRow
                  label={t('Customization_Multiline')}
                  description={t('Customization_MultilineHelp')}
                  checked={Boolean(draft.multiline)}
                  onChange={(checked) => patch({ multiline: checked })}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    size="small" fullWidth label={t('Customization_ValidationPattern')}
                    value={draft.pattern ?? ''} onChange={(event) => patch({ pattern: event.target.value })}
                  />
                  <NumberField
                    size="small" fullWidth label={t('Customization_CharLimit')}
                    allowDecimal={false}
                    value={draft.charLimit}
                    onChange={(charLimit) => patch({ charLimit })}
                  />
                </Stack>
              </>
            )}

            {(draft.type === 'Number' || draft.type === 'Range') && (
              <ToggleRow
                label={t('Customization_AllowDecimals')}
                description={draft.type === 'Range'
                  ? t('Customization_RangeDecimalsHelp')
                  : t('Customization_AllowDecimalsHelp')}
                checked={Boolean(draft.decimal)}
                onChange={(checked) => patch({ decimal: checked, ...(checked ? {} : whole(draft)) })}
              />
            )}

            {draft.type === 'Barcode' && (
              <>
                {/* A labelled control rather than a loose heading, so it reads as one field
                    alongside the inputs above and below it. */}
                <FormControl fullWidth>
                  <FormLabel sx={{ typography: 'caption', color: 'text.secondary', mb: 1 }}>
                    {t('Customization_AcceptedFormats')}
                  </FormLabel>
                  <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                    {barcodeFormats.map((format) => {
                      const selected = (draft.formats ?? []).includes(format);
                      return (
                        <Chip
                          key={format}
                          size="small"
                          label={format}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => patch({
                            formats: selected
                              ? (draft.formats ?? []).filter((current) => current !== format)
                              : [...(draft.formats ?? []), format],
                          })}
                        />
                      );
                    })}
                  </Stack>
                </FormControl>
                <TextField
                  size="small" label={t('Customization_ValidationPattern')}
                  value={draft.pattern ?? ''} onChange={(event) => patch({ pattern: event.target.value })}
                />
              </>
            )}

            {isChoice && (
              <FormControl fullWidth>
                <FormLabel sx={{ typography: 'caption', color: 'text.secondary', mb: 1 }}>
                  {t('Customization_Choices')}
                </FormLabel>

                <Stack spacing={1.5}>
                  {/* Keyed by the option's own id, not its position: with an index key React
                      reuses a removed row's state for whatever slides up into its place. */}
                  {options.map((option, index) => {
                    const isDefault = Boolean(option.id && chosen.includes(option.id));

                    return (
                      <Stack key={option.id ?? index} direction="row" spacing={1} alignItems="center">
                        <IconButton
                          size="small"
                          color={isDefault ? 'warning' : 'default'}
                          title={isDefault ? t('Customization_ClearDefault') : t('Customization_MarkDefault')}
                          onClick={() => toggleDefault(option)}
                        >
                          {isDefault ? <StarIcon /> : <StarOIcon />}
                        </IconButton>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <LocalizedTextField
                            label={`${t('Customization_Choice')} ${index + 1}`}
                            value={option.label}
                            onChange={(label) => setOption(index, { ...option, label })}
                          />
                        </Box>

                        <IconButton
                          size="small"
                          disabled={options.length <= 1}
                          title={options.length <= 1 ? t('Customization_LastChoice') : t('Common_Delete')}
                          onClick={() => removeOption(index)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Stack>
                    );
                  })}
                </Stack>

                <Box sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={<PlusIcon />}
                    sx={buttonStyles}
                    onClick={() => patch({ options: [...options, { id: newEntityId(), label: {} }] })}
                  >
                    {t('Customization_AddChoice')}
                  </Button>
                </Box>

                <FormHelperText sx={{ mx: 0, mt: 1 }}>
                  {draft.type === 'List' ? t('Customization_StarSingleDefault') : t('Customization_StarMultiDefault')}
                </FormHelperText>
              </FormControl>
            )}

            {draft.type === 'Range' && (
              <Stack direction="row" spacing={2}>
                <NumberField
                  size="small" fullWidth label={t('Customization_Min')}
                  allowDecimal={Boolean(draft.decimal)}
                  value={draft.min} onChange={(min) => patch({ min })}
                />
                <NumberField
                  size="small" fullWidth label={t('Customization_Max')}
                  allowDecimal={Boolean(draft.decimal)}
                  value={draft.max} onChange={(max) => patch({ max })}
                />
                <NumberField
                  size="small" fullWidth label={t('Common_Default')}
                  allowDecimal={Boolean(draft.decimal)}
                  value={typeof draft.value === 'number' ? draft.value : undefined}
                  onChange={(value) => patch({ value })}
                />
              </Stack>
            )}

            {draft.type === 'Toggle' && (
              <ToggleRow
                label={t('Customization_DefaultState')}
                description={t('Customization_DefaultStateHelp')}
                checked={Boolean(draft.value)}
                onChange={(checked) => patch({ value: checked })}
              />
            )}

          </Stack>

          <Divider sx={{ my: 3 }} />

          <Step n={3}>{t('Customization_Behaviour')}</Step>
          <Stack spacing={1.5}>
            <ToggleRow
              label={t('Customization_Required')}
              description={t('Customization_RequiredHelp')}
              checked={draft.required}
              onChange={(checked) => patch({ required: checked })}
            />
            <ToggleRow
              label={t('Customization_ReadOnly')}
              description={t('Customization_ReadOnlyHelp')}
              checked={draft.readOnly}
              onChange={(checked) => patch({ readOnly: checked })}
            />
            <ToggleRow
              label={t('Customization_ShowOnReports')}
              description={t('Customization_ShowOnReportsHelp')}
              checked={draft.showOnReports}
              onChange={(checked) => patch({ showOnReports: checked })}
            />
          </Stack>
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={drawerFooterStyles}>
          {/* The caption takes the free space, which is what pushes the buttons right. */}
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {t('Customization_FieldSavesTo', [typeName])}
          </Typography>
          <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={!canSave}
            onClick={() => onSave(draft, target)}
            sx={buttonStyles}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FieldDrawer;
